// @flow

import React from "react";

import {
    Stack,
    Button,
    Dialog,
    Alert,
    AppBar,
    Toolbar,
    IconButton,
    Typography,
    TextField,
    List,
    ListItemButton,
    ListItemText,
    Slide,
    Snackbar,
    CircularProgress,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

import axios from 'axios';

import { TENANT } from "../../constants";

import { useAppContext } from "../../context/AppContext";

import { useLanguage } from "../../i18n";

import { expandUrl } from "../../utils";

const Transition = React.forwardRef(function Transition(props, ref) {
    return <Slide direction="up" ref={ref} {...props} />;
});

const PickerDialog = ({ open, title, items, loading, onClose, onRender, onSelect, onSearch }) => {
    return (
        <Dialog
            fullScreen
            open={open}
            onClose={onClose}
            TransitionComponent={Transition}
        >
            <AppBar position="sticky">
                <Toolbar>
                    <IconButton edge="start" color="inherit" onClick={onClose}>
                        <CloseIcon />
                    </IconButton>
                    <Typography sx={{ ml: 2 }} variant="h6">
                        {title}
                    </Typography>
                </Toolbar>
            </AppBar>

            <TextField
                autoFocus
                placeholder={`${onSearch ? '' : ''}${title.toLowerCase()}`}
                fullWidth
                onChange={(e) => onSearch(e.target.value)}
                InputProps={{
                    endAdornment: loading ? <CircularProgress size={20} /> : null,
                }}
                sx={{ p: 2 }}
            />

            <List>
                {items.map((item) => (
                    <ListItemButton
                        key={item.id}
                        onClick={() => onSelect(item)}
                    >
                        <ListItemText
                            primary={onRender(item)}
                            secondary={item.secondary}
                        />
                    </ListItemButton>
                ))}
            </List>
        </Dialog>
    );
};

// Type of the selected drop-off point. FAN Courier sends its location type (locker | office | pudo)
// as code, other couriers (Sameday easybox) send lockers only.
const placeType = (place: any): string => {
    return (place && (place.code === 'office' || place.code === 'pudo')) ? 'office' : 'locker';
};

type Props = {
    stepNo: number,
    activeStep: number,
    handleNext: () => void,
}

const PickupLocationStep = (props: Props): any => {
    const { state, setState } = useAppContext();
    const { t } = useLanguage();

    const [step, setStep] = React.useState(null);

    const [countries, setCountries] = React.useState([]);
    const [loadingCountries, setLoadingCountries] = React.useState(false);

    const [cities, setCities] = React.useState([]);
    const [loadingCities, setLoadingCities] = React.useState(false);

    const [lockers, setLockers] = React.useState([]);
    const [loadingLockers, setLoadingLockers] = React.useState(false);

    const [inProgress, setInProgress] = React.useState(false);

    const country = state.returnRequest?.pickupAddress?.country;
    const city = state.returnRequest?.pickupAddress?.city;
    const locker = state.returnRequest?.pickupAddress?.place;

    const [snackbarMessage, setSnackbarMessage] = React.useState(null);
    const handleShowError = (error: string) => {
        setSnackbarMessage(error);
    };
    const handleSnackbarClose = (_, reason) => {
        // Prevent closing on clickaway (optional)
        if (reason === "clickaway") return;
        setSnackbarMessage(null);
    };

    const setPickupAddress = async (country: any, city: any, locker: any) => {
        let newState = state;

        if (country != null) {
            newState = {
                ...newState,
                returnRequest: {
                    ...newState.returnRequest,
                    pickupAddress: {
                        ...newState.returnRequest.pickupAddress,
                        country
                    }
                }
            }
        }
        if (city != null) {
            newState = {
                ...newState,
                returnRequest: {
                    ...newState.returnRequest,
                    pickupAddress: {
                        ...newState.returnRequest.pickupAddress,
                        city,
                        postalCode: city.postalCode
                    }
                }
            }
        }
        if (locker != null) {
            newState = {
                ...newState,
                returnRequest: {
                    ...newState.returnRequest,
                    pickupAddress: {
                        ...newState.returnRequest.pickupAddress,
                        place: locker,
                        street: {
                            id: '',
                            name: (locker.address) ? locker.address : '',
                        }
                    }
                }
            }
        }

        const pickupAddress = newState.returnRequest.pickupAddress;

        if (pickupAddress.country != null && pickupAddress.city != null && 
                pickupAddress.place != null) {
            setInProgress(true);
            const url = state.returnRequest._links.changePickupAddress.href;
            try {
                const response = await axios.post(
                    url,
                    {
                        pickupAddress: {
                            ...pickupAddress,
                            ref1: placeType(pickupAddress.place)
                        }
                    },
                    {
                        headers: {
                            "X-Tenant": TENANT,
                            "X-Sale-Order-No": state.saleOrderNo,
                            "X-Phone-Last4": state.phoneLast4,
                        }
                    },
                );
                
                setState({ ...state, returnRequest: response.data });

                // move next
                // props.handleNext();
            } catch (error) {
                console.error("Error update picking address:", error);

                const errorData = error.response?.data;
                if (errorData?.causes && Array.isArray(errorData.causes)) {
                    const messages = errorData.causes
                        .map((cause) => cause.message)
                        .join("; ");
                    handleShowError(messages);
                } else {
                    handleShowError(
                        errorData?.message || t('errorOccurred'),
                    );
                }
            } finally {
                setInProgress(false);
            }
        } else {
            setState(newState);    
        }
    }

    React.useEffect(() => {
        console.log('State updated:', state);
    }, [state]);

    const loadCountries = async (query = "") => {
        try {
            setLoadingCountries(true);

            const url = expandUrl(state.returnRequest._links.countries.href, {
                name: query
            });

            const response = await axios.get(
                url, 
                {
                    headers: {
                        "X-Tenant": TENANT,
                        "X-Sale-Order-No": state.saleOrderNo,
                        "X-Phone-Last4": state.phoneLast4,
                    },
                    params: { } 
                }
            );

            setCountries(response.data?._embedded?.addressCountries ? response.data._embedded.addressCountries : []);
        } catch (err) {
            console.error(err);
            const errorData = err.response?.data;
            if (errorData?.causes && Array.isArray(errorData.causes)) {
                const messages = errorData.causes.map(cause => cause.message).join('; ');
                handleShowError(messages);
            } else {
                handleShowError(errorData?.message || t('errorOccurred'));
            }
        } finally {
            setLoadingCountries(false);
        }
    };

    const loadCities = async (query = "") => {
        if (!country || !country.id) {
            return;
        }

        try {
            setLoadingCities(true);

            const url = expandUrl(state.returnRequest._links.cities.href, {
                countryId: country ? country.id : '',
                name: query
            });

            const response = await axios.get(
                url, 
                {
                    headers: {
                        "X-Tenant": TENANT,
                        "X-Sale-Order-No": state.saleOrderNo,
                        "X-Phone-Last4": state.phoneLast4,
                    },
                    params: { } 
                }
            );

            setCities(response.data?._embedded?.addressCityResourceList ? response.data._embedded.addressCityResourceList : []);
        } catch (err) {
            console.error(err);
            const errorData = err.response?.data;
            if (errorData?.causes && Array.isArray(errorData.causes)) {
                const messages = errorData.causes.map(cause => cause.message).join('; ');
                handleShowError(messages);
            } else {
                handleShowError(errorData?.message || t('errorOccurred'));
            }
        } finally {
            setLoadingCities(false);
        }
    };

    const loadLockers = async (query = "") => {
        if (!city || !city.id) {
            return;
        }

        try {
            setLoadingLockers(true);

            const url = expandUrl(state.returnRequest._links.offices.href, {
                countryId: country ? country.id : '',
                cityId: city ? city.id : '',
                name: query
            });

            const response = await axios.get(
                url, 
                {
                    headers: {
                        "X-Tenant": TENANT,
                        "X-Sale-Order-No": state.saleOrderNo,
                        "X-Phone-Last4": state.phoneLast4,
                    },
                    params: { } 
                }
            );

            setLockers(response.data?._embedded?.addressOfficeResourceList ? response.data._embedded.addressOfficeResourceList : []);
        } catch (err) {
            console.error(err);
            const errorData = err.response?.data;
            if (errorData?.causes && Array.isArray(errorData.causes)) {
                const messages = errorData.causes.map(cause => cause.message).join('; ');
                handleShowError(messages);
            } else {
                handleShowError(errorData?.message || t('errorOccurred'));
            }
        } finally {
            setLoadingLockers(false);
        }
    };

    const isNew = state.returnRequest?.state === 'NEW';

    return (
        <>
            <Snackbar
                open={snackbarMessage != null}
                autoHideDuration={4000}
                onClose={handleSnackbarClose}
                anchorOrigin={{
                    vertical: "bottom",
                    horizontal: "center",
                }}
            >
                <Alert
                    onClose={handleSnackbarClose}
                    severity="error"
                    variant="filled"
                    sx={{ width: "100%" }}
                >
                    {snackbarMessage}
                </Alert>
            </Snackbar>

            <Stack spacing={2}>
                <Typography variant="caption">{t('selectPickupLocation')}</Typography>

                <Button
                    variant="outlined"
                    disabled={!isNew || inProgress}
                    onClick={() => {
                        loadCountries();
                        setStep("country");
                    }}
                >
                    {country && country.id ? country.name : t('selectCountry')}
                </Button>

                <Button
                    variant="outlined"
                    disabled={!isNew || !country || inProgress}
                    onClick={() => {
                        loadCities();
                        setStep("city");
                    }}
                >
                    {city && city.id ? city.name : t('selectCity')}
                </Button>

                <Button
                    variant="outlined"
                    
                    disabled={!isNew || !city || inProgress}
                    startIcon={
                        inProgress ? <CircularProgress size={20} color="inherit" /> : null
                    }

                    onClick={() => {
                        loadLockers();
                        setStep("locker");
                    }}
                >
                    {locker && locker.id ? locker.name : t('selectLocker')}
                </Button>

                {(props.activeStep === props.stepNo && country != null && city != null && locker != null) ? 
                    (<Button
                        variant="contained"
                        onClick={props.handleNext}
                        sx={{ mt: 1, mr: 1 }}
                    >
                        {t('continue')}
                    </Button>) : null
                }
            </Stack>

            {/* COUNTRY */}
            <PickerDialog
                open={step === "country"}
                title={t('country')}
                items={countries}
                loading={loadingCountries}
                onSearch={loadCountries}
                onRender={(item) => item.name}
                onClose={() => setStep(null)}
                onSelect={(value) => {
                    setPickupAddress(value, {}, {});
                    setStep(null);
                }}
            />

            {/* CITY */}
            <PickerDialog
                open={step === "city"}
                title={t('city')}
                items={cities}
                loading={loadingCities}
                onSearch={loadCities}
                onRender={(item) => `${item.name} (${item.postalCode})`}
                onClose={() => setStep(null)}
                onSelect={(value) => {
                    setPickupAddress(null, value, {});
                    setStep(null);
                }}
            />

            {/* LOCKER */}
            <PickerDialog
                open={step === "locker"}
                title={t('locker')}
                items={lockers}
                loading={loadingLockers}
                onSearch={loadLockers}
                onRender={(item) => item.name}
                onClose={() => setStep(null)}
                onSelect={(value) => {
                    setPickupAddress(null, null, value);
                    setStep(null);
                }}
            />
        </>
    );
};

export default PickupLocationStep;
